import React, { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Info, XCircle, HelpCircle } from 'lucide-react';

/**
 * 2026-08-15 (kullanici talebi): Tarayicinin siyah zeminli standart
 * alert() / confirm() kutusu hem kotu duruyor hem de kullanicilar
 * okumadan geciyordu.
 *
 * Bu bilesen uygulama ici, renk kodlu ve baslikli bir modal saglar.
 * Kullanimi imperative — sayfalarda ekstra state tutmaya gerek yok:
 *
 *   import { uyar, onayla, basarili } from '../components/GlobalModal';
 *
 *   uyar('Kayit bulunamadi');                       // alert() yerine
 *   if (!(await onayla('Silinsin mi?'))) return;    // confirm() yerine
 *
 * <GlobalModal /> App.tsx icinde BIR KEZ mount edilir.
 */

export type ModalTuru = 'bilgi' | 'uyari' | 'tehlike' | 'basari' | 'soru';

export interface ModalSatiri {
  etiket: string;
  deger: string;
}

interface ModalIstegi {
  tur: ModalTuru;
  baslik: string;
  mesaj: string;
  satirlar?: ModalSatiri[];
  notlar?: string[];
  onayMetni?: string;
  iptalMetni?: string;
  tekButon: boolean;
  cevapla: (kabul: boolean) => void;
}

// --- Basit yayin mekanizmasi (store yerine; tek dinleyici yeter) ---
let dinleyici: ((istek: ModalIstegi | null) => void) | null = null;
const kuyruk: ModalIstegi[] = [];

function gonder(istek: ModalIstegi) {
  if (dinleyici) dinleyici(istek);
  else kuyruk.push(istek);   // modal henuz mount olmadiysa beklet
}

interface Secenekler {
  baslik?: string;
  satirlar?: ModalSatiri[];
  notlar?: string[];
  onayMetni?: string;
  iptalMetni?: string;
}

/** alert() yerine — bilgilendirme. Beklemek zorunda degilsiniz. */
export function uyar(mesaj: string, secenek: Secenekler & { tur?: ModalTuru } = {}) {
  return new Promise<boolean>(cevapla => {
    gonder({
      tur: secenek.tur ?? 'uyari',
      baslik: secenek.baslik ?? 'Uyarı',
      mesaj,
      satirlar: secenek.satirlar,
      notlar: secenek.notlar,
      onayMetni: secenek.onayMetni ?? 'Tamam',
      tekButon: true,
      cevapla,
    });
  });
}

/** Hata bildirimi (kirmizi). */
export function hata(mesaj: string, secenek: Secenekler = {}) {
  return uyar(mesaj, { ...secenek, tur: 'tehlike', baslik: secenek.baslik ?? 'Hata' });
}

/** Basari bildirimi (yesil). */
export function basarili(mesaj: string, secenek: Secenekler = {}) {
  return uyar(mesaj, { ...secenek, tur: 'basari', baslik: secenek.baslik ?? 'İşlem Tamam' });
}

/** confirm() yerine — true/false doner. await ile kullanin. */
export function onayla(mesaj: string, secenek: Secenekler & { tur?: ModalTuru } = {}) {
  return new Promise<boolean>(cevapla => {
    gonder({
      tur: secenek.tur ?? 'soru',
      baslik: secenek.baslik ?? 'Onay',
      mesaj,
      satirlar: secenek.satirlar,
      notlar: secenek.notlar,
      onayMetni: secenek.onayMetni ?? 'Devam Et',
      iptalMetni: secenek.iptalMetni ?? 'Vazgeç',
      tekButon: false,
      cevapla,
    });
  });
}

/** Yikici islem onayi (kirmizi). */
export function sil(mesaj: string, secenek: Secenekler = {}) {
  return onayla(mesaj, {
    ...secenek,
    tur: 'tehlike',
    baslik: secenek.baslik ?? 'Silme Onayı',
    onayMetni: secenek.onayMetni ?? 'Sil',
  });
}

const STIL: Record<ModalTuru, { bas: string; dugme: string; ikon: React.ReactNode }> = {
  bilgi:    { bas: 'bg-slate-700', dugme: 'bg-slate-700 hover:bg-slate-800', ikon: <Info size={20} className="text-white" /> },
  uyari:    { bas: 'bg-amber-500', dugme: 'bg-amber-600 hover:bg-amber-700', ikon: <AlertTriangle size={20} className="text-white" /> },
  tehlike:  { bas: 'bg-red-600',   dugme: 'bg-red-600 hover:bg-red-700',     ikon: <XCircle size={20} className="text-white" /> },
  basari:   { bas: 'bg-green-600', dugme: 'bg-green-600 hover:bg-green-700', ikon: <CheckCircle2 size={20} className="text-white" /> },
  soru:     { bas: 'bg-blue-600',  dugme: 'bg-blue-600 hover:bg-blue-700',   ikon: <HelpCircle size={20} className="text-white" /> },
};

const GlobalModal: React.FC = () => {
  const [istek, setIstek] = useState<ModalIstegi | null>(null);

  useEffect(() => {
    dinleyici = setIstek;
    // Mount oncesi gelen istekleri sirayla goster
    if (kuyruk.length > 0) setIstek(kuyruk.shift()!);
    return () => { dinleyici = null; };
  }, []);

  const kapat = (kabul: boolean) => {
    istek?.cevapla(kabul);
    // Kuyrukta bekleyen varsa onu ac, yoksa kapat
    setIstek(kuyruk.length > 0 ? kuyruk.shift()! : null);
  };

  useEffect(() => {
    if (!istek) return;
    const tus = (e: KeyboardEvent) => {
      if (e.key === 'Escape') kapat(false);
      if (e.key === 'Enter') kapat(true);
    };
    window.addEventListener('keydown', tus);
    return () => window.removeEventListener('keydown', tus);
  }, [istek]);

  if (!istek) return null;
  const stil = STIL[istek.tur];

  return (
    <div
      className="fixed inset-0 z-[100] bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={() => kapat(false)}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className={`${stil.bas} px-5 py-4 flex items-center gap-3`}>
          {stil.ikon}
          <h3 className="text-white font-black text-base">{istek.baslik}</h3>
        </div>

        <div className="p-5 max-h-[60vh] overflow-y-auto">
          <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{istek.mesaj}</p>

          {istek.satirlar && istek.satirlar.length > 0 && (
            <div className="mt-4 bg-slate-50 border border-slate-200 rounded-xl divide-y divide-slate-100">
              {istek.satirlar.map((r, i) => (
                <div key={i} className="flex justify-between gap-3 px-3 py-2 text-sm">
                  <span className="text-slate-500">{r.etiket}</span>
                  <span className="font-bold text-slate-800 font-mono text-right">{r.deger}</span>
                </div>
              ))}
            </div>
          )}

          {istek.notlar && istek.notlar.length > 0 && (
            <ul className="mt-3 space-y-1">
              {istek.notlar.map((n, i) => (
                <li key={i} className="text-xs text-slate-500 flex gap-2">
                  <span className="text-slate-400">•</span>
                  <span>{n}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="px-5 pb-5 flex gap-3">
          {!istek.tekButon && (
            <button
              onClick={() => kapat(false)}
              className="flex-1 py-2.5 border border-slate-200 text-slate-600 rounded-xl font-bold hover:bg-slate-50 transition-all"
            >
              {istek.iptalMetni ?? 'Vazgeç'}
            </button>
          )}
          <button
            autoFocus
            onClick={() => kapat(true)}
            className={`flex-1 py-2.5 ${stil.dugme} text-white rounded-xl font-bold transition-all`}
          >
            {istek.onayMetni ?? 'Tamam'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GlobalModal;
